// =============================================================================
// features/conversations/components/HandoffBriefingModal.tsx — Modal do
// briefing de handoff (F37-S06, doc 28 §2.2/§3/§6, plano
// docs/planejamento-briefing-handoff-regua-qualificacao.md §2.2).
//
// Na primeira abertura humana pós-handoff, entrega em 4 blocos fixos quem é
// o cliente, o que quer, onde a IA parou e o parecer estruturado do LLM —
// nunca um parágrafo solto. Gatilho, guard contra reabertura e ack vivem em
// `ConversationPanel.tsx` — este componente só RENDERIZA o briefing já
// resolvido (id) e chama `onClose` em Esc/overlay/botão (a decisão de
// disparar o ack é do chamador).
//
// Estados do bloco 4 (Parecer da IA):
//   loading                    → skeleton do modal inteiro (briefing ainda não
//                                 chegou do cache/rede — raro, a ref leve do
//                                 detalhe já garante `id` válido).
//   generating                 → skeleton do parecer + "gerando parecer…".
//                                 Troca ao vivo via socket `briefing_ready`
//                                 (useConversationSocket invalida a query).
//   ready                      → parecer completo (resumo, ação sugerida,
//                                 pontos de atenção, tom, confiança).
//   ready_without_assessment   → aviso neutro "Parecer indisponível — a régua
//                                 acima é confiável" (LLM falhou, nunca bloqueia).
//
// A11y: `role="dialog"`, `aria-modal`, foco preso (Tab cicla dentro do
// painel, devolvido ao elemento anterior ao fechar), `Esc` fecha. Mobile:
// sheet full-height (`inset-0`); desktop: painel centrado com `--elev-5`.
//
// DS (doc 18): tokens de cor/espaçamento/elevação — zero hex hardcoded.
// Bricolage no título (font-display), Geist no corpo (font-sans), JetBrains
// Mono nos valores monetários (font-mono).
//
// LGPD (doc 17, doc 28 §3 D6): `assessment` nunca contém CPF/telefone/renda
// bruta (garantido pelo backend) — este componente só exibe, nunca loga.
// Rodapé fixo de transparência (Art. 20 LGPD — decisão automatizada não
// vinculante): "Parecer gerado por IA — confira antes de agir".
// =============================================================================

import type {
  AssessmentConfidenceLevel,
  CustomerTone,
  HandoffOutcome,
} from '@elemento/shared-types';
import * as React from 'react';
import { createPortal } from 'react-dom';

import type { BadgeVariant } from '../../../components/ui/Badge';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { cn } from '../../../lib/cn';
import { formatBRL } from '../../../lib/format/money';
import { useBriefing } from '../queries';
import type {
  Briefing,
  BriefingRequestSnapshot,
  HandoffAssessment,
  QualificationStep,
} from '../types';

import { formatRelativeTime, humanizeHandoffReason, resolveStepVisual } from './QualificationRuler';

// ---------------------------------------------------------------------------
// Vocabulário — rótulos pt-BR + mapeamento semântico de cor (doc 28 §3 D4)
// ---------------------------------------------------------------------------

const OUTCOME_LABELS: Readonly<Record<HandoffOutcome, string>> = {
  qualified: 'Qualificado',
  partial: 'Parcial',
  customer_requested_human: 'Pediu atendente',
  out_of_scope: 'Fora do escopo',
  technical_failure: 'Falha técnica',
};

/** `info` = `--info`, que no doc 18 tem o MESMO valor de `--brand-azul` em ambos os temas. */
const OUTCOME_BADGE_VARIANT: Readonly<Record<HandoffOutcome, BadgeVariant>> = {
  qualified: 'success',
  partial: 'warning',
  customer_requested_human: 'info',
  out_of_scope: 'neutral',
  technical_failure: 'danger',
};

const TONE_LABELS: Readonly<Record<CustomerTone, string>> = {
  interested: 'Interessado',
  hesitant: 'Hesitante',
  in_a_hurry: 'Com pressa',
  frustrated: 'Frustrado',
  confused: 'Confuso',
};

const CONFIDENCE_LABELS: Readonly<Record<AssessmentConfidenceLevel, string>> = {
  high: 'Confiança alta',
  medium: 'Confiança média',
  low: 'Confiança baixa',
};

const CONFIDENCE_BADGE_VARIANT: Readonly<Record<AssessmentConfidenceLevel, BadgeVariant>> = {
  high: 'success',
  medium: 'warning',
  low: 'neutral',
};

// ---------------------------------------------------------------------------
// Helpers puros — testados isoladamente em
// __tests__/HandoffBriefingModal.test.ts (mesma convenção de
// QualificationRuler.test.ts: sem jsdom/RTL neste projeto).
// ---------------------------------------------------------------------------

export type AssessmentBlockState = 'loading' | 'generating' | 'ready' | 'ready_without_assessment';

/**
 * Deriva o estado do bloco 4 (Parecer da IA) a partir do briefing carregado.
 * `loading`: briefing ainda não resolveu (raro — o gatilho em
 * `ConversationPanel` só monta este modal com um `briefingId` já conhecido).
 */
export function resolveAssessmentBlockState(
  briefing: Briefing | null | undefined,
): AssessmentBlockState {
  if (!briefing) return 'loading';
  if (briefing.status === 'ready' && briefing.assessment !== null) return 'ready';
  if (briefing.status === 'ready_without_assessment') return 'ready_without_assessment';
  return 'generating';
}

/** Valor de uma etapa da régua pelo `key` — `null` quando a etapa não tem valor (doc 28 §3 D6). */
export function stepValue(
  steps: readonly QualificationStep[],
  key: QualificationStep['key'],
): string | null {
  return steps.find((s) => s.key === key)?.value ?? null;
}

/**
 * Rótulo da etapa onde a progressão parou — a primeira etapa `!== 'done'`
 * fora `handoff` (que é sempre `done` no snapshot pós-handoff, doc 28 §2.1).
 * `null` quando as 8 etapas anteriores ao handoff estão completas.
 */
export function findStoppedAtLabel(steps: readonly QualificationStep[]): string | null {
  const stopped = steps.find((s) => s.key !== 'handoff' && s.status !== 'done');
  return stopped?.label ?? null;
}

/** "R$ 15.000 em 24 meses" / partes ausentes caem para "—" — nunca undefined solto na tela. */
export function formatRequestSummary(request: BriefingRequestSnapshot): {
  readonly amount: string;
  readonly term: string;
  readonly objective: string;
  readonly installment: string | null;
} {
  return {
    amount: request.amount !== null ? formatBRL(request.amount) : '—',
    term: request.termMonths !== null ? `${request.termMonths.toString()} meses` : '—',
    objective: request.objective ?? '—',
    installment:
      request.installment !== null ? `Parcela ≈ ${formatBRL(request.installment)}` : null,
  };
}

// ---------------------------------------------------------------------------
// Foco preso + Escape — dialog modal (WCAG 2.4.3). Inline (single-use) em vez
// de reaproveitar `features/sales/components/useFocusTrap.ts` (não exportado
// no barrel `features/sales/index.ts` para consumo cross-feature) — mesma
// lógica: Tab cicla dentro do painel, Escape fecha, foco devolvido ao sair.
// ---------------------------------------------------------------------------

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function useDialogChrome(containerRef: React.RefObject<HTMLDivElement>, onClose: () => void): void {
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const previouslyFocused = document.activeElement as HTMLElement | null;

    function getFocusable(): HTMLElement[] {
      return container
        ? Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
        : [];
    }

    const initial = getFocusable();
    (initial[0] ?? container).focus();

    function handleKeyDown(e: KeyboardEvent): void {
      if (!container) return;
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const current = getFocusable();
      const first = current[0];
      const last = current[current.length - 1];
      if (!first || !last) {
        e.preventDefault();
        return;
      }
      const activeEl = document.activeElement;
      if (e.shiftKey) {
        if (activeEl === first || !container.contains(activeEl)) {
          e.preventDefault();
          last.focus();
        }
      } else if (activeEl === last || !container.contains(activeEl)) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (previouslyFocused && document.contains(previouslyFocused)) previouslyFocused.focus();
    };
    // containerRef é estável (ref) — onClose muda pouco (useCallback do chamador).
  }, [containerRef, onClose]);
}

// ---------------------------------------------------------------------------
// Sub-componentes visuais
// ---------------------------------------------------------------------------

function CloseIcon(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      className="w-4 h-4"
      aria-hidden="true"
    >
      <path d="M4 4l8 8M12 4l-8 8" />
    </svg>
  );
}

function ModalHeader({
  briefing,
  onClose,
}: {
  briefing: Briefing | null;
  onClose: () => void;
}): React.JSX.Element {
  const at = briefing ? new Date(briefing.handoffAt) : null;
  const validAt = at && !Number.isNaN(at.getTime()) ? at : null;
  const reasonLabel = briefing ? humanizeHandoffReason(briefing.handoffReason) : null;
  const outcome = briefing?.assessment?.outcome ?? null;

  return (
    <div
      className="flex items-start justify-between gap-3 px-5 py-4 border-b border-border-subtle shrink-0"
      style={{ background: 'var(--bg-elev-2)' }}
    >
      <div className="min-w-0 flex-1 flex flex-col gap-1.5">
        <div className="flex items-center gap-2 flex-wrap">
          <h2
            id="handoff-briefing-title"
            className="font-display font-bold text-ink"
            style={{ fontSize: 'var(--text-lg)', letterSpacing: '-0.03em' }}
          >
            Briefing do handoff
          </h2>
          {outcome && (
            <Badge variant={OUTCOME_BADGE_VARIANT[outcome]}>{OUTCOME_LABELS[outcome]}</Badge>
          )}
        </div>
        <p className="font-sans text-ink-2" style={{ fontSize: 'var(--text-xs)' }}>
          Transferido pela Ana Clara
          {validAt && <> · {formatRelativeTime(validAt, new Date())}</>}
          {reasonLabel && <> · Motivo: {reasonLabel}</>}
        </p>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Fechar briefing"
        className={cn(
          'w-8 h-8 flex items-center justify-center rounded-sm shrink-0',
          'text-ink-3 hover:text-ink hover:bg-surface-hover',
          'transition-all duration-fast ease-out',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/20',
        )}
      >
        <CloseIcon />
      </button>
    </div>
  );
}

function BodySkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4 px-5 py-5 animate-pulse" aria-hidden="true">
      <div className="h-3 w-1/3 rounded bg-surface-muted" />
      <div className="h-16 rounded-md bg-surface-muted" />
      <div className="h-3 w-1/2 rounded bg-surface-muted" />
      <div className="h-10 rounded-md bg-surface-muted" />
      <div className="h-20 rounded-md bg-surface-muted" />
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <span
      className="font-sans font-semibold uppercase text-ink-3"
      style={{ fontSize: 10, letterSpacing: '0.1em' }}
    >
      {children}
    </span>
  );
}

/** Bloco 2 — "Quem é" (dados) + régua compacta (reuso das cores de `resolveStepVisual`, S05). */
function WhoIsAndRuler({ briefing }: { briefing: Briefing }): React.JSX.Element {
  const name = stepValue(briefing.qualification, 'contact') ?? '—';
  const city = stepValue(briefing.qualification, 'city') ?? '—';
  const profile = stepValue(briefing.qualification, 'profile') ?? '—';
  const activity = stepValue(briefing.qualification, 'activity') ?? '—';
  const stoppedAtLabel = findStoppedAtLabel(briefing.qualification);
  const done = briefing.qualification.filter((s) => s.status === 'done').length;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 px-5 py-4 border-b border-border-subtle">
      <div className="flex flex-col gap-2 min-w-0">
        <SectionLabel>Quem é</SectionLabel>
        <dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1 mt-1">
          {[
            ['Nome', name],
            ['Cidade', city],
            ['Perfil', profile],
            ['Atividade', activity],
          ].map(([label, value]) => (
            <React.Fragment key={label}>
              <dt className="font-sans text-ink-3" style={{ fontSize: 'var(--text-xs)' }}>
                {label}
              </dt>
              <dd
                className="font-sans font-medium text-ink truncate"
                style={{ fontSize: 'var(--text-xs)' }}
              >
                {value}
              </dd>
            </React.Fragment>
          ))}
        </dl>
      </div>

      <div className="flex flex-col gap-2 min-w-0">
        <div className="flex items-center justify-between">
          <SectionLabel>Régua</SectionLabel>
          <span className="font-mono font-semibold text-ink-2" style={{ fontSize: 11 }}>
            {done} de {briefing.qualification.length}
          </span>
        </div>
        <ol
          aria-label="Etapas da régua de qualificação no momento do handoff"
          className="flex items-center gap-1.5 mt-1"
        >
          {briefing.qualification.map((step) => {
            const visual = resolveStepVisual(step.status);
            return (
              <li key={step.key} title={step.value ? `${step.label}: ${step.value}` : step.label}>
                <span
                  aria-hidden="true"
                  style={{
                    display: 'block',
                    width: 11,
                    height: 11,
                    borderRadius: '50%',
                    background: visual.dashed ? 'transparent' : visual.colorVar,
                    border: visual.dashed ? `1.5px dashed ${visual.colorVar}` : 'none',
                  }}
                />
              </li>
            );
          })}
        </ol>
        <p className="font-sans text-ink-2 mt-0.5" style={{ fontSize: 'var(--text-xs)' }}>
          {stoppedAtLabel ? <>Parou em: {stoppedAtLabel}</> : 'Qualificação completa'}
        </p>
      </div>
    </div>
  );
}

function DataChip({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <span
      className="font-mono font-semibold text-ink rounded-sm"
      style={{
        fontSize: 'var(--text-xs)',
        padding: '5px 10px',
        background: 'var(--surface-muted)',
        border: '1px solid var(--border-subtle)',
        boxShadow: 'var(--elev-1)',
      }}
    >
      {children}
    </span>
  );
}

/** Bloco 3 — "O que quer" (chips de valor, prazo, objetivo, parcela). */
function WhatTheyWant({ request }: { request: BriefingRequestSnapshot }): React.JSX.Element {
  const summary = formatRequestSummary(request);
  return (
    <div className="flex flex-col gap-2 px-5 py-4 border-b border-border-subtle">
      <SectionLabel>O que quer</SectionLabel>
      <div className="flex flex-wrap gap-2 mt-1">
        <DataChip>{summary.amount}</DataChip>
        <DataChip>{summary.term}</DataChip>
        <span
          className="font-sans font-semibold text-ink rounded-sm"
          style={{
            fontSize: 'var(--text-xs)',
            padding: '5px 10px',
            background: 'var(--surface-muted)',
            border: '1px solid var(--border-subtle)',
            boxShadow: 'var(--elev-1)',
          }}
        >
          {summary.objective}
        </span>
        {summary.installment && <DataChip>{summary.installment}</DataChip>}
      </div>
    </div>
  );
}

function AssessmentGenerating(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <span
          aria-hidden="true"
          className="animate-pulse"
          style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--brand-azul)' }}
        />
        <p className="font-sans font-medium text-ink-2" style={{ fontSize: 'var(--text-xs)' }}>
          Gerando parecer…
        </p>
      </div>
      <div className="h-4 w-4/5 rounded bg-surface-muted animate-pulse" aria-hidden="true" />
      <div className="h-10 rounded-md bg-surface-muted animate-pulse" aria-hidden="true" />
    </div>
  );
}

function AssessmentUnavailable(): React.JSX.Element {
  return (
    <div
      role="status"
      className="flex items-start gap-2.5 rounded-md px-3.5 py-3"
      style={{ background: 'var(--surface-muted)', border: '1px solid var(--border-subtle)' }}
    >
      <p className="font-sans text-ink-2" style={{ fontSize: 'var(--text-xs)' }}>
        Parecer indisponível — a régua acima é confiável.
      </p>
    </div>
  );
}

function AssessmentReady({ assessment }: { assessment: HandoffAssessment }): React.JSX.Element {
  return (
    <div className="flex flex-col gap-3">
      <p className="font-sans text-ink" style={{ fontSize: 'var(--text-sm)', lineHeight: 1.5 }}>
        {assessment.summary}
      </p>

      <div
        className="rounded-md px-3.5 py-3"
        style={{
          background: 'color-mix(in srgb, var(--brand-azul) 8%, var(--bg-elev-1))',
          border: '1px solid color-mix(in srgb, var(--brand-azul) 24%, transparent)',
        }}
      >
        <p
          className="font-sans font-semibold text-azul"
          style={{ fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase' }}
        >
          Próxima ação sugerida
        </p>
        <p className="font-sans font-medium text-ink mt-1" style={{ fontSize: 'var(--text-sm)' }}>
          {assessment.recommendedAction}
        </p>
      </div>

      {assessment.attentionPoints.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <SectionLabel>Pontos de atenção</SectionLabel>
          <ul className="flex flex-col gap-1 mt-0.5 list-disc pl-4">
            {assessment.attentionPoints.map((point) => (
              <li
                key={point}
                className="font-sans text-ink-2"
                style={{ fontSize: 'var(--text-xs)' }}
              >
                {point}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mt-1">
        {assessment.customerTone.map((tone) => (
          <Badge key={tone} variant="neutral">
            {TONE_LABELS[tone]}
          </Badge>
        ))}
        <Badge variant={CONFIDENCE_BADGE_VARIANT[assessment.confidence]}>
          {CONFIDENCE_LABELS[assessment.confidence]}
        </Badge>
      </div>
    </div>
  );
}

/** Bloco 4 — "Parecer da IA" — os 4 estados de `AssessmentBlockState`. */
function AssessmentBlock({ briefing }: { briefing: Briefing }): React.JSX.Element {
  const state = resolveAssessmentBlockState(briefing);
  return (
    <div className="flex flex-col gap-3 px-5 py-4">
      <SectionLabel>Parecer da IA</SectionLabel>
      {state === 'generating' && <AssessmentGenerating />}
      {state === 'ready_without_assessment' && <AssessmentUnavailable />}
      {state === 'ready' && briefing.assessment && (
        <AssessmentReady assessment={briefing.assessment} />
      )}
    </div>
  );
}

function ModalFooter({ onClose }: { onClose: () => void }): React.JSX.Element {
  return (
    <div
      className="flex items-center justify-between gap-3 px-5 py-4 border-t border-border-subtle shrink-0"
      style={{ background: 'var(--bg-elev-2)' }}
    >
      <p className="font-sans text-ink-3" style={{ fontSize: 10 }}>
        Parecer gerado por IA — confira antes de agir.
      </p>
      <Button variant="primary" size="sm" onClick={onClose}>
        Abrir conversa
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

export interface HandoffBriefingModalProps {
  /** UUID da conversa — usado para buscar o briefing (`GET .../briefing`). */
  readonly conversationId: string;
  /**
   * Chamado em Esc/overlay/botão de fechar/"Abrir conversa" — o CHAMADOR
   * decide se dispara o ack (`ConversationPanel`, guard de 1 chamada por
   * `briefingId`). Este componente nunca chama `useAckBriefing` diretamente.
   */
  readonly onClose: () => void;
}

/**
 * HandoffBriefingModal — modal de 4 blocos do briefing de handoff (doc 28 §2.2).
 *
 * Renderizado por `ConversationPanel` apenas quando há um `briefingId` ainda
 * não visto — o gatilho e o guard de reabertura vivem lá. Este componente é
 * "burro" por design: busca o briefing pelo `conversationId` e desenha os
 * blocos fixos, delegando o fechamento (e o ack) ao `onClose`.
 */
export function HandoffBriefingModal({
  conversationId,
  onClose,
}: HandoffBriefingModalProps): React.JSX.Element {
  const { data, isLoading } = useBriefing(conversationId);
  const briefing = data?.briefing ?? null;

  const containerRef = React.useRef<HTMLDivElement>(null);
  useDialogChrome(containerRef, onClose);

  return createPortal(
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-40"
        style={{ background: 'rgba(10, 18, 40, 0.50)' }}
        aria-hidden="true"
        onClick={onClose}
      />

      {/* Painel — mobile: sheet full-height. Desktop: centrado, elev-5. */}
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="handoff-briefing-title"
        tabIndex={-1}
        className={cn(
          'fixed z-50 inset-0 md:inset-auto',
          'md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2',
          'md:w-[min(720px,92vw)] md:max-h-[88vh]',
          'flex flex-col overflow-hidden',
          'md:rounded-md border border-border',
          'focus:outline-none',
        )}
        style={{ background: 'var(--bg-elev-1)', boxShadow: 'var(--elev-5)' }}
      >
        <ModalHeader briefing={briefing} onClose={onClose} />

        <div className="flex-1 overflow-y-auto">
          {isLoading || !briefing ? (
            <BodySkeleton />
          ) : (
            <>
              <WhoIsAndRuler briefing={briefing} />
              <WhatTheyWant request={briefing.request} />
              <AssessmentBlock briefing={briefing} />
            </>
          )}
        </div>

        <ModalFooter onClose={onClose} />
      </div>
    </>,
    document.body,
  );
}
